import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { aiSearchAutoEvalService } from "../services/aiSearchAutoEvalService";
import { aiSearchAutoEvalDisplayId } from "./AiSearchAutoEvalCommonStore";
import { AiSearchAutoEval } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

export class AiSearchAutoEvalViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = aiSearchAutoEvalService;
  aiSearchAutoEvalInstance: AiSearchAutoEval | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  runIdValue: string = "";
  queryIndexValue: number = 0;
  conditionValue: string = "";
  includeSchemaValue: boolean = false;
  includeExamplesValue: boolean = false;
  queryValue: string = "";
  pathTypeValue: string = "";
  expectedToolValue: string = "";
  expectedEntityValue: string = "";
  actualToolValue: string = "";
  actualEntityValue: string = "";
  actualArgsValue: string = "{}";
  l1PassValue: boolean = false;
  l2PassValue: boolean = false;
  errorTypeValue: string = "";
  totalTimeMsValue: number = 0;
  llmCallCountValue: number = 0;
  toolCallCountValue: number = 0;
  tokenCountValue: number | undefined = undefined;
  llmResponseValue: string = "";
  toolCallsValue: string = "{}";
  evaluatedAtValue: Date | null = null;
  hadDisambiguationValue: boolean = false;
  disambiguationTypeValue: string = "";
  disambiguationResponseValue: string = "";
  patternsAppliedValue: string[] = [];
  l3EfficiencyValue: number | undefined = undefined;
  l4RedundancyValue: number | undefined = undefined;
  l5DisambiguationValue: number | undefined = undefined;
  l6ResponseQualityValue: number | undefined = undefined;
  overallScoreValue: number | undefined = undefined;
  judgeEvaluationValue: string = "{}";
  redundantCallsValue: number | undefined = undefined;
  actualCallCountValue: number | undefined = undefined;
  optimalCallCountValue: number | undefined = undefined;
  sequenceMatchedOptimalValue: boolean = false;
  responseValue: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadAiSearchAutoEvalById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.aiSearchAutoEvalOne({ id });
    this.aiSearchAutoEvalInstance = result.aiSearchAutoEvalOne;
    if (this.aiSearchAutoEvalInstance) {
      this.loadValues(this.aiSearchAutoEvalInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.aiSearchAutoEvalInstance) {
      return aiSearchAutoEvalDisplayId(
        this.aiSearchAutoEvalInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.AiSearchAutoEvalSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.aiSearchAutoEvalInstance = null;
    this.runIdValue = "";
    this.queryIndexValue = 0;
    this.conditionValue = "";
    this.includeSchemaValue = false;
    this.includeExamplesValue = false;
    this.queryValue = "";
    this.pathTypeValue = "";
    this.expectedToolValue = "";
    this.expectedEntityValue = "";
    this.actualToolValue = "";
    this.actualEntityValue = "";
    this.actualArgsValue = "{}";
    this.l1PassValue = false;
    this.l2PassValue = false;
    this.errorTypeValue = "";
    this.totalTimeMsValue = 0;
    this.llmCallCountValue = 0;
    this.toolCallCountValue = 0;
    this.tokenCountValue = undefined;
    this.llmResponseValue = "";
    this.toolCallsValue = "{}";
    this.evaluatedAtValue = null;
    this.hadDisambiguationValue = false;
    this.disambiguationTypeValue = "";
    this.disambiguationResponseValue = "";
    this.patternsAppliedValue = [];
    this.l3EfficiencyValue = undefined;
    this.l4RedundancyValue = undefined;
    this.l5DisambiguationValue = undefined;
    this.l6ResponseQualityValue = undefined;
    this.overallScoreValue = undefined;
    this.judgeEvaluationValue = "{}";
    this.redundantCallsValue = undefined;
    this.actualCallCountValue = undefined;
    this.optimalCallCountValue = undefined;
    this.sequenceMatchedOptimalValue = false;
    this.responseValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelAiSearchAutoEval() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editAiSearchAutoEval() {
    const id = this.aiSearchAutoEvalInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/aiSearchAutoEval/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printAiSearchAutoEval(handlePrint: () => void): void {
    handlePrint();
  }

  *closeAiSearchAutoEval(): any {
    yield this.close();
  }

  loadValues(instance: AiSearchAutoEval) {
    this.idValue = instance.id;
    this.runIdValue = instance.runId;
    this.queryIndexValue = instance.queryIndex;
    this.conditionValue = instance.condition;
    this.includeSchemaValue = instance.includeSchema;
    this.includeExamplesValue = instance.includeExamples;
    this.queryValue = instance.query;
    this.pathTypeValue = instance.pathType || "";
    this.expectedToolValue = instance.expectedTool;
    this.expectedEntityValue = instance.expectedEntity;
    this.actualToolValue = instance.actualTool || "";
    this.actualEntityValue = instance.actualEntity || "";
    this.actualArgsValue = instance.actualArgs || "";
    this.l1PassValue = instance.l1Pass;
    this.l2PassValue = instance.l2Pass || false;
    this.errorTypeValue = instance.errorType || "";
    this.totalTimeMsValue = instance.totalTimeMs;
    this.llmCallCountValue = instance.llmCallCount;
    this.toolCallCountValue = instance.toolCallCount;
    this.tokenCountValue = instance.tokenCount || undefined;
    this.llmResponseValue = instance.llmResponse || "";
    this.toolCallsValue = instance.toolCalls || "";
    this.evaluatedAtValue = new Date(instance.evaluatedAt);
    this.hadDisambiguationValue = instance.hadDisambiguation;
    this.disambiguationTypeValue = instance.disambiguationType || "";
    this.disambiguationResponseValue = instance.disambiguationResponse || "";
    this.patternsAppliedValue = instance.patternsApplied || [];
    this.l3EfficiencyValue = instance.l3Efficiency || undefined;
    this.l4RedundancyValue = instance.l4Redundancy || undefined;
    this.l5DisambiguationValue = instance.l5Disambiguation || undefined;
    this.l6ResponseQualityValue = instance.l6ResponseQuality || undefined;
    this.overallScoreValue = instance.overallScore || undefined;
    this.judgeEvaluationValue = instance.judgeEvaluation || "";
    this.redundantCallsValue = instance.redundantCalls || undefined;
    this.actualCallCountValue = instance.actualCallCount || undefined;
    this.optimalCallCountValue = instance.optimalCallCount || undefined;
    this.sequenceMatchedOptimalValue = instance.sequenceMatchedOptimal || false;
    this.responseValue = instance.response || "";
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadAiSearchAutoEvalById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
